import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { db } from "../firebase";
import "./AdminAchievements.css";

function AdminAchievements() {
  const { t, i18n } = useTranslation();

  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    titleAr: "",
    titleEn: "",
    descriptionAr: "",
    descriptionEn: "",
    year: "",
    image: "",
  });

  const [preview, setPreview] = useState("");

  const isArabic = i18n.language === "ar";

  /* ================================
     FETCH
  ================================= */

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      setLoading(true);

      const q = query(
        collection(db, "achievements"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setAchievements(data);
    } catch (error) {
      console.error("Error fetching achievements:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================================
     FORM
  ================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm({
      titleAr: "",
      titleEn: "",
      descriptionAr: "",
      descriptionEn: "",
      year: "",
      image: "",
    });

    setPreview("");
    setEditingId(null);
    setShowForm(false);
  };

  /* ================================
     IMAGE
  ================================= */

  const handleImage = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert(t("achievements.invalidImage"));
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        const maxSize = 900;

        let width = img.width;
        let height = img.height;

        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        const canvas = document.createElement("canvas");

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0, width, height);

        const compressedImage = canvas.toDataURL("image/jpeg", 0.72);

        setForm((prev) => ({
          ...prev,
          image: compressedImage,
        }));

        setPreview(compressedImage);
      };

      img.src = event.target.result;
    };

    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const removeImage = () => {
    setForm((prev) => ({
      ...prev,
      image: "",
    }));

    setPreview("");
  };

  /* ================================
     EDIT
  ================================= */

  const handleEdit = (achievement) => {
    setEditingId(achievement.id);

    setForm({
      titleAr: achievement.titleAr || "",
      titleEn: achievement.titleEn || "",
      descriptionAr: achievement.descriptionAr || "",
      descriptionEn: achievement.descriptionEn || "",
      year: achievement.year || "",
      image: achievement.image || "",
    });

    setPreview(achievement.image || "");
    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ================================
     SAVE
  ================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.titleAr.trim() || !form.titleEn.trim()) {
      alert(t("achievements.requiredTitle"));
      return;
    }

    if (!form.descriptionAr.trim() || !form.descriptionEn.trim()) {
      alert(t("achievements.requiredDescription"));
      return;
    }

    try {
      setSaving(true);

      const achievementData = {
        titleAr: form.titleAr.trim(),
        titleEn: form.titleEn.trim(),
        descriptionAr: form.descriptionAr.trim(),
        descriptionEn: form.descriptionEn.trim(),
        year: form.year.trim(),
        image: form.image || "",
      };

      if (editingId) {
        await updateDoc(
          doc(db, "achievements", editingId),
          achievementData
        );

        alert(t("achievements.successEdit"));
      } else {
        await addDoc(collection(db, "achievements"), {
          ...achievementData,
          createdAt: serverTimestamp(),
        });

        alert(t("achievements.successAdd"));
      }

      await fetchAchievements();
      resetForm();
    } catch (error) {
      console.error("Error saving achievement:", error);
      alert(t("achievements.saveError"));
    } finally {
      setSaving(false);
    }
  };

  /* ================================
     DELETE
  ================================= */

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      t("achievements.confirmDelete")
    );

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "achievements", id));

      setAchievements((prev) =>
        prev.filter((achievement) => achievement.id !== id)
      );

      alert(t("achievements.successDelete"));
    } catch (error) {
      console.error("Error deleting achievement:", error);
      alert(t("achievements.deleteError"));
    }
  };

  return (
    <div
      className={`admin-achievements-page ${
        isArabic ? "rtl" : "ltr"
      }`}
      dir={isArabic ? "rtl" : "ltr"}
    >
      {/* HEADER */}

      <div className="admin-achievements-header">
        <div>
          <span className="admin-header-badge">
            🏆 {t("achievements.dashboardBadge")}
          </span>

          <h1>{t("achievements.dashboardTitle")}</h1>

          <p>{t("achievements.dashboardSubtitle")}</p>
        </div>

        {!showForm && (
          <button
            className="add-achievement-btn"
            onClick={() => setShowForm(true)}
          >
            <span>＋</span>
            {t("achievements.add")}
          </button>
        )}
      </div>

      {/* FORM */}

      {showForm && (
        <section className="achievement-form-card">
          <div className="form-header">
            <div>
              <h2>
                {editingId
                  ? t("achievements.edit")
                  : t("achievements.add")}
              </h2>

              <p>{t("achievements.formSubtitle")}</p>
            </div>

            <button
              type="button"
              className="close-form-btn"
              onClick={resetForm}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {/* ARABIC TITLE */}

              <div className="form-group">
                <label>
                  {t("achievements.titleAr")}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  name="titleAr"
                  value={form.titleAr}
                  onChange={handleChange}
                  placeholder={t("achievements.titleArPlaceholder")}
                  dir="rtl"
                />
              </div>

              {/* ENGLISH TITLE */}

              <div className="form-group">
                <label>
                  {t("achievements.titleEn")}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  name="titleEn"
                  value={form.titleEn}
                  onChange={handleChange}
                  placeholder={t("achievements.titleEnPlaceholder")}
                  dir="ltr"
                />
              </div>

              {/* ARABIC DESCRIPTION */}

              <div className="form-group form-group-full">
                <label>
                  {t("achievements.descriptionAr")}
                  <span>*</span>
                </label>

                <textarea
                  name="descriptionAr"
                  value={form.descriptionAr}
                  onChange={handleChange}
                  placeholder={t(
                    "achievements.descriptionArPlaceholder"
                  )}
                  dir="rtl"
                  rows="5"
                />
              </div>

              {/* ENGLISH DESCRIPTION */}

              <div className="form-group form-group-full">
                <label>
                  {t("achievements.descriptionEn")}
                  <span>*</span>
                </label>

                <textarea
                  name="descriptionEn"
                  value={form.descriptionEn}
                  onChange={handleChange}
                  placeholder={t(
                    "achievements.descriptionEnPlaceholder"
                  )}
                  dir="ltr"
                  rows="5"
                />
              </div>

              {/* YEAR */}

              <div className="form-group">
                <label>{t("achievements.year")}</label>

                <input
                  type="text"
                  name="year"
                  value={form.year}
                  onChange={handleChange}
                  placeholder="2026"
                  maxLength="4"
                />
              </div>

              {/* IMAGE */}

              <div className="form-group form-group-full">
                <label>{t("achievements.image")}</label>

                <label
                  className={`admin-image-upload ${
                    preview ? "has-image" : ""
                  }`}
                >
                  {preview ? (
                    <>
                      <img
                        src={preview}
                        alt="Preview"
                        className="admin-image-preview"
                      />

                      <div className="admin-image-overlay">
                        <span>📷</span>
                        <p>{t("achievements.changeImage")}</p>
                      </div>

                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeImage();
                        }}
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <div className="admin-upload-placeholder">
                      <span className="upload-icon">📷</span>

                      <strong>
                        {t("achievements.chooseImage")}
                      </strong>

                      <small>
                        JPG / PNG / WEBP
                      </small>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImage}
                    className="admin-image-file-input"
                  />
                </label>
              </div>
            </div>

            {/* FORM BUTTONS */}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={resetForm}
                disabled={saving}
              >
                {t("achievements.cancel")}
              </button>

              <button
                type="submit"
                className="save-achievement-btn"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="button-spinner"></span>
                    {t("achievements.saving")}
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    {editingId
                      ? t("achievements.saveChanges")
                      : t("achievements.save")}
                  </>
                )}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* LIST */}

      <section className="admin-achievements-list">
        <div className="list-title">
          <div>
            <h2>{t("achievements.allAchievements")}</h2>

            <p>
              {achievements.length}{" "}
              {t("achievements.achievementCount")}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
            <p>{t("achievements.loading")}</p>
          </div>
        ) : achievements.length === 0 ? (
          <div className="admin-empty">
            <div>🏆</div>

            <h3>{t("achievements.noAchievements")}</h3>

            <p>{t("achievements.noAchievementsText")}</p>

            <button
              onClick={() => setShowForm(true)}
              className="empty-add-btn"
            >
              ＋ {t("achievements.add")}
            </button>
          </div>
        ) : (
          <div className="admin-achievements-grid">
            {achievements.map((achievement, index) => {
              const title = isArabic
                ? achievement.titleAr
                : achievement.titleEn;

              const description = isArabic
                ? achievement.descriptionAr
                : achievement.descriptionEn;

              return (
                <article
                  className="admin-achievement-card"
                  key={achievement.id}
                >
                  <div className="admin-card-image">
                    {achievement.image ? (
                      <img
                        src={achievement.image}
                        alt={title}
                      />
                    ) : (
                      <div className="admin-no-image">
                        🏆
                      </div>
                    )}

                    <div className="admin-card-number">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    {achievement.year && (
                      <div className="admin-card-year">
                        {achievement.year}
                      </div>
                    )}
                  </div>

                  <div className="admin-card-content">
                    <h3>{title}</h3>

                    <p>{description}</p>

                    <div className="admin-card-actions">
                      <button
                        className="edit-btn"
                        onClick={() =>
                          handleEdit(achievement)
                        }
                      >
                        ✏️ {t("achievements.edit")}
                      </button>

                      <button
                        className="delete-btn"
                        onClick={() =>
                          handleDelete(achievement.id)
                        }
                      >
                        🗑️ {t("achievements.delete")}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default AdminAchievements;